import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseService } from '../../services/base.service';
import { environment } from '../../../environments/environment';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { RegisterForm } from './register.interface';
import { DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations, PHONES_FOR_INPUT } from '../../enum/constants';
import { AuthService } from '../../services/auth.service';
import { IError } from '../../interfaces';
import { NotificationService } from '../../services/notifications.service';
import { decodeBase64, trimValidator } from '../../helpers';
import { KeycloakService } from 'keycloak-angular';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RegisterComponent implements OnInit {
  public homeUrl: string = `${window.location.protocol}//${environment.web_url}`;
  public registerForm!: FormGroup<RegisterForm>;
  public isRegistering: boolean = false;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    public readonly baseService: BaseService,
    private readonly notificationService: NotificationService,
    private readonly keycloakService: KeycloakService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.initForm();
    this.authService.initUtm();
    this.prefillInvitedUser();
  }

  /** Invite links carry `?user_id=`: fill in what the inviter already entered and lock those fields */
  private prefillInvitedUser(): void {
    const userId = this.route.snapshot.queryParamMap.get('user_id');
    if (!userId) return;

    this.authService.getInvitedUser(userId).subscribe({
      next: (user) => {
        const values: Partial<Record<keyof RegisterForm, string | null | undefined>> = {
          first_name: user?.first_name,
          last_name: user?.last_name,
          phone_number: user?.phone,
          email: user?.email,
        };

        for (const [key, value] of Object.entries(values) as [keyof RegisterForm, string | null | undefined][]) {
          if (!value?.trim()) continue;

          const control = this.registerForm.controls[key];
          control.setValue(value.trim());
          control.disable();
        }
      },
      // The link may be outdated; the form just stays empty and editable
      error: () => {},
    });
  }

  private initForm() {
    this.registerForm = new FormGroup<RegisterForm>(
      {
        first_name: new FormControl('', [Validators.required, trimValidator]),
        last_name: new FormControl('', [Validators.required, trimValidator]),
        phone_number: new FormControl('', [Validators.required]),
        email: new FormControl('', [Validators.email, Validators.required]),
        password: new FormControl('', [Validators.minLength(6), Validators.required, trimValidator]),
        password_confirmation: new FormControl('', [Validators.required, trimValidator]),
      },
      {
        validators: this.passwordMatch('password', 'password_confirmation'),
      },
    );
  }

  private passwordMatch(password: string, confirmPassword: string): ValidatorFn {
    return (formGroup: AbstractControl): Record<string, any> | null => {
      const passwordControl = formGroup.get(password);
      const confirmPasswordControl = formGroup.get(confirmPassword);

      if (!passwordControl || !confirmPasswordControl) {
        return null;
      }

      if (confirmPasswordControl.errors && !confirmPasswordControl.errors['passwordMismatch']) {
        return null;
      }

      if (passwordControl.value !== confirmPasswordControl.value) {
        confirmPasswordControl.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      } else {
        confirmPasswordControl.setErrors(null);
        return null;
      }
    };
  }

  public onLogin() {
    this.router.navigate(['/login']);
  }

  public onSubmit() {
    this.isRegistering = true;

    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      this.isRegistering = false;
      return;
    }

    const payload: any = this.registerForm.getRawValue();
    delete payload.password_confirmation;

    const utm_source = localStorage.getItem('utm_source');
    if (utm_source) {
      payload.utm_source = utm_source;
    }
    const userId = this.route.snapshot.queryParamMap.get('user_id');
    if (userId) {
      payload.user_id = userId;
    }

    this.authService.register(payload).subscribe({
      next: async (res) => {
        const token = res.access_token;
        const refreshToken = res.refresh_token;
        const keycloakOptions = {
          config: { ...environment.keycloak },
          initOptions: {
            checkLoginIframe: false,
            token: token,
            refreshToken: refreshToken,
          },
        };
        try {
          await this.keycloakService.init(keycloakOptions);

          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);

          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_created,
          );
          this.isRegistering = false;

          if (this.baseService.getCompany()) {
            const redirectCode = this.route.snapshot.queryParamMap.get('redirect_code');
            if (redirectCode) {
              const redirectUrl = decodeBase64(redirectCode);

              if (redirectUrl) window.location.href = redirectUrl;
              else this.router.navigate(['/crm']);
            }
          } else {
            this.router.navigate(['/companies']);
          }
        } catch (e: any) {
          this.router.navigate(['/login']);
        }
      },
      error: (e: any) => {
        (e.error?.errors || []).forEach((err: IError) => {
          if (err.code === 'NOT_VERIFIED_PHONE') {
            this.authService.openVerifyPhoneModal(payload.email, payload.password);
            return;
          }
          this.notificationService.showError(
            activeTranslations.notifications_texts.error.error,
            ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR],
          );
        });
        this.isRegistering = false;
      },
    });
  }

  protected readonly PHONES_FOR_INPUT = PHONES_FOR_INPUT;
}
